import React, {useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useQuery} from "@tanstack/react-query";
import {gridBackgroundQueryOptions} from "../background-catalog";
import {buildBackgroundsOverride} from "../../libs/bgTransform";
import {resolveBannerSelection} from "../../data/template-availability";
import BgSelector from "../../layout/BgSelector";
import {type BannerInstance} from "../document";
import {useBannerRuntime} from "../BannerRuntime";

export function WorkspaceAssetControls({banner, onGenerate}: {
  banner: BannerInstance; onGenerate: (count?: number, stops?: number) => void;
}) {
  const {definition, product} = resolveBannerSelection(banner);
  const client = useQueryClient();
  const runtime = useBannerRuntime();
  const queryOptions = gridBackgroundQueryOptions(definition.channel, banner.contentType, product.code);
  const gallery = useQuery({...queryOptions, select: (data) => data.backgrounds, enabled: !definition.templateId.startsWith("SpeakerPhoto") && definition.channel !== "ads"});
  const backgrounds = gallery.data ?? [];
  const allBgUrls = useMemo(() => buildBackgroundsOverride(backgrounds) ?? [], [backgrounds]);
  return <>
    <span className="label">Background {gallery.isPending ? "(Loading...)" : ""}</span>
    <BgSelector isLoading={gallery.isPending} allBgUrls={allBgUrls} linkGallery={backgrounds} controlled={{
      option: runtime.backgroundOption ?? banner.appearance.backgroundOption,
      onSelect: (value: string | number) => {
        if (value === "generative") {runtime.cancelUpload("background"); onGenerate(); return;}
        const background = backgrounds.find((bg) => bg.variation === Number(value));
        if (!background) throw new Error(`Background ${value} is unavailable`);
        void runtime.selectBackground(String(background.variation));
      },
      onUpload: (file: File) => runtime.upload("background", file),
      onRegenerate: () => onGenerate(),
      onCount: (count: number) => onGenerate(count),
      onStops: (stops: number) => onGenerate(banner.appearance.generativeCount, stops),
    }} />
    {gallery.isError && <p role="alert">Background library is unavailable. <button onClick={() => void client.invalidateQueries({queryKey: queryOptions.queryKey})}>Retry</button></p>}
    {runtime.backgroundOption !== null && <p role="status">Applying background…</p>}
  </>;
}
